import React, { useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileInvoiceDollar, faTrash, faArrowUpRightFromSquare, faFileLines, faXmark,
    faCircleCheck, faPaperclip, faReceipt,
} from '@fortawesome/free-solid-svg-icons';

import StockModal from './StockModal';
import { exactTime } from './stockShared';

/**
 * File the supplier's documents against the purchase they belong to.
 *
 * SEVERAL FILES, ONE SAVE (owner, 2026-09-07: "allow multiple invoices to be
 * uploaded for one order"). A purchase can arrive as two invoices, an invoice
 * plus a delivery note, or a receipt photographed page by page — so the picker
 * takes any number of files and one "Save" files them all. Each goes straight
 * to S3 through the v2 presign rail (kind `stock-invoice`) and only the URLs
 * are posted to us, in one request, so the order's history reads "3 files
 * filed" rather than three separate entries.
 *
 * THE DOCUMENT IS THE WHOLE RECORD. No amount, no note, no second form to
 * fill in — the invoice already says what it cost, and asking a manager to
 * retype that is how a figure ends up disagreeing with the PDF beside it.
 *
 * FILED AGAINST THE ORDER. A supplier invoice covers the whole purchase, so
 * every line on that order is documented by construction. The dialog stays
 * open after saving so what was just filed is visible and more can follow.
 */
const MAX_BYTES = 25 * 1024 * 1024;
const sizeLabel = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

// onRecord(invoice): book THIS invoice in Zoho — one expense per supplier
// invoice (owner, 2026-09-12), so each row carries its own Zoho state.
export default function InvoiceModal({ order, onClose, onSaved, onRecord }) {
    const [picked, setPicked] = useState([]);       // File[] waiting to go up
    const [progress, setProgress] = useState(null); // { done, total } while uploading
    const [error, setError] = useState('');

    // Filing order (oldest first): row one here is "invoice 1 of N" in the
    // expense dialog and in the Zoho description. The server sends newest first.
    const existing = [...(order.invoices || [])].reverse();
    const saving = progress !== null;

    const pick = (e) => {
        const chosen = Array.from(e.target.files || []);
        // Same file twice (a re-pick) must not file twice.
        setPicked((prev) => {
            const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
            return [...prev, ...chosen.filter((f) => !seen.has(`${f.name}:${f.size}`))];
        });
        setError('');
        e.target.value = '';
    };

    const unpick = (idx) => setPicked((prev) => prev.filter((_, i) => i !== idx));

    const save = async (e) => {
        e.preventDefault();
        if (saving) return;
        if (!picked.length) { setError('Choose the invoice file(s) first.'); return; }
        const big = picked.find((f) => f.size > MAX_BYTES);
        if (big) { setError(`${big.name} is over 25 MB — send a smaller scan.`); return; }

        setError('');
        setProgress({ done: 0, total: picked.length });
        const uploaded = [];
        try {
            for (const file of picked) {
                const presign = await axios.post('/api/uploads/v2/presign', {
                    kind: 'stock-invoice',
                    request_id: order.id,
                    file_name: file.name,
                    content_type: file.type || 'application/octet-stream',
                });
                // withCredentials MUST be off: the bucket's CORS sends no
                // Allow-Credentials, so the browser blocks a credentialed PUT.
                await axios.put(presign.data.url, file, {
                    withCredentials: false,
                    headers: { 'Content-Type': file.type || 'application/octet-stream' },
                });
                uploaded.push({ file_url: presign.data.file_url, file_name: file.name, file_size: file.size });
                setProgress({ done: uploaded.length, total: picked.length });
            }

            await axios.post(`/api/stock/orders/${order.id}/invoices`, { files: uploaded });

            setPicked([]);
            setProgress(null);
            await onSaved({ keepOpen: true, added: uploaded.length });
        } catch (err) {
            setProgress(null);
            // Files already on S3 but not yet filed are simply re-sent on the
            // next try — the presign rail hands out fresh keys, nothing is lost.
            setError(err.response?.data?.error || 'The upload did not go through. Try again.');
        }
    };

    const remove = async (invoiceId) => {
        try {
            await axios.delete(`/api/stock/orders/invoices/${invoiceId}`);
            await onSaved({ keepOpen: true });
        } catch (err) {
            setError(err.response?.data?.error || 'Could not remove that file.');
        }
    };

    return (
        <StockModal
            title={`Invoices — order #${order.id}`}
            subtitle={
                `${(order.lines || []).length} item${(order.lines || []).length === 1 ? '' : 's'}`
                + `${order.reference ? ` · ref ${order.reference}` : ''}`
            }
            onClose={onClose}
            width={540}
        >
            {existing.length > 0 && (
                <div className="stk__field">
                    <span className="stk__fieldLabel">
                        Filed on this order · {existing.length}
                    </span>
                    {existing.length > 1 && onRecord && (
                        <span className="stk__fieldHint stk__fieldHint--above">
                            Each supplier invoice is recorded in Zoho on its own, under its own vendor.
                        </span>
                    )}
                    <div className="stk__invoiceList">
                        {existing.map((v) => (
                            <div key={v.id} className="stk__invoiceRow">
                                <FontAwesomeIcon icon={faFileLines} className="stk__invoiceIcon" />
                                <span className="stk__invoiceMain">
                                    <span className="stk__invoiceName">{v.file_name || 'Invoice'}</span>
                                    <span className="stk__invoiceMeta">
                                        {v.uploaded_by_name || 'Filed'} · {exactTime(v.uploaded_at)}
                                        {v.zoho_vendor_name ? ` · ${v.zoho_vendor_name}` : ''}
                                    </span>
                                </span>
                                {/* Same chip the buying links use — one visual language for
                                    "this opens a thing in a new tab", wherever it appears. */}
                                <span className="stk__linkWrap">
                                    <a
                                        href={v.file_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="stk__link stk__link--primary"
                                    >
                                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                        <span className="stk__linkLabel">Open</span>
                                    </a>
                                </span>
                                {v.zoho_expense_id ? (
                                    <a
                                        className="stk__docZoho"
                                        href={v.zoho_expense_url || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={`Recorded ${exactTime(v.zoho_expense_at)} — opens in Zoho Books`}
                                    >
                                        <FontAwesomeIcon icon={faCircleCheck} />
                                        {v.zoho_is_sandbox ? 'In Zoho (sandbox)' : 'In Zoho'}
                                        {v.zoho_expense_total != null ? ` · AED ${Number(v.zoho_expense_total).toFixed(2)}` : ''}
                                    </a>
                                ) : v.covered_by_invoice_id ? (
                                    <span
                                        className="stk__docZoho stk__docZoho--covered"
                                        title="Went up to Zoho attached to another invoice on this order"
                                    >
                                        <FontAwesomeIcon icon={faPaperclip} />
                                        {' With '}
                                        {existing.find((x) => x.id === v.covered_by_invoice_id)?.file_name || 'another invoice'}
                                    </span>
                                ) : v.in_zoho ? (
                                    <span className="stk__docZoho">
                                        <FontAwesomeIcon icon={faCircleCheck} /> In Zoho
                                    </span>
                                ) : onRecord ? (
                                    <button
                                        type="button"
                                        className="stk__docRecord"
                                        onClick={() => onRecord(v)}
                                        disabled={saving}
                                    >
                                        <FontAwesomeIcon icon={faReceipt} /> Record in Zoho
                                    </button>
                                ) : null}
                                {/* A document behind an expense in the books stays. */}
                                {!v.in_zoho && (
                                    <button
                                        type="button"
                                        className="stk__iconBtn stk__iconBtn--danger"
                                        onClick={() => remove(v.id)}
                                        title="Remove this file"
                                        aria-label="Remove this file"
                                        disabled={saving}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="stk__orderLines">
                {(order.lines || []).map((l) => (
                    <div key={l.request_id} className="stk__orderLine">
                        <span className="stk__orderName">{l.item_name}</span>
                        <span className="stk__orderCat">{l.category_name}</span>
                    </div>
                ))}
            </div>

            <form onSubmit={save}>
                <label className="stk__field">
                    <span className="stk__fieldLabel">
                        <FontAwesomeIcon icon={faFileInvoiceDollar} />
                        {existing.length ? ' Add more files' : ' Invoices or receipts'}
                    </span>
                    <input
                        type="file"
                        className="stk__file"
                        accept="application/pdf,image/*"
                        multiple
                        onChange={pick}
                        disabled={saving}
                    />
                    <span className="stk__fieldHint">
                        PDFs or photos — pick several at once. Up to 25 MB each.
                    </span>
                </label>

                {picked.length > 0 && (
                    <div className="stk__invoiceList">
                        {picked.map((f, idx) => (
                            <div key={`${f.name}:${f.size}`} className="stk__invoiceRow">
                                <FontAwesomeIcon icon={faFileLines} className="stk__invoiceIcon" />
                                <span className="stk__invoiceMain">
                                    <span className="stk__invoiceName">{f.name}</span>
                                    <span className="stk__invoiceMeta">
                                        {sizeLabel(f.size)}
                                        {progress && idx < progress.done ? ' · uploaded' : ''}
                                        {progress && idx === progress.done ? ' · uploading…' : ''}
                                    </span>
                                </span>
                                <button
                                    type="button"
                                    className="stk__iconBtn"
                                    onClick={() => unpick(idx)}
                                    title="Don't upload this one"
                                    aria-label="Don't upload this one"
                                    disabled={saving}
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {error ? <p className="stk__formError">{error}</p> : null}

                <div className="stk__modalActions">
                    <button type="button" className="stk__btn stk__btn--ghost" onClick={onClose} disabled={saving}>
                        Close
                    </button>
                    <button type="submit" className="stk__btn stk__btn--primary" disabled={saving || !picked.length}>
                        {saving
                            ? `Uploading ${Math.min(progress.done + 1, progress.total)} of ${progress.total}…`
                            : picked.length > 1 ? `Save ${picked.length} files` : 'Save invoice'}
                    </button>
                </div>
            </form>
        </StockModal>
    );
}
